import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX_MS = 180000;
const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isMobile = /Android|iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
const isChromium = /Chrome|Chromium|Edg\//.test(ua) && !/OPR\/|Brave/.test(ua);
// Live preview only where browser speech + recording can share the mic reliably
const liveCapable = () => !isMobile && isChromium && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  return ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", "audio/ogg;codecs=opus"].find((m) => MediaRecorder.isTypeSupported?.(m)) || "";
};
const extFor = (mime) => (mime.includes("mp4") || mime.includes("aac") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm");

const micErrorMessage = (err) => {
  const inFrame = window.self !== window.top;
  switch (err?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return inFrame
        ? "Microphone is blocked inside this preview window. Open the app in a new tab and allow the microphone."
        : "Microphone permission is blocked. Click the mic/lock icon in the address bar, allow the microphone, then try again.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone found. Plug one in or check your system sound settings.";
    case "NotReadableError":
    case "AbortError":
      return "Your microphone is busy in another app (Zoom, Meet…). Close it and try again.";
    default:
      return "Couldn't start the microphone. Please type your idea instead.";
  }
};

export function useDictation({ getBase, onText }) {
  const [status, setStatus] = useState("idle"); // idle | requesting | recording | transcribing
  const [analyser, setAnalyser] = useState(null);
  const r = useRef({});

  const cleanup = useCallback(() => {
    const s = r.current;
    clearTimeout(s.maxTimer);
    clearInterval(s.levelTimer);
    s.stopLive = true;
    try { s.rec?.stop(); } catch { /* already stopped */ }
    s.stream?.getTracks().forEach((t) => t.stop());
    s.ctx?.close().catch(() => {});
    r.current = {};
    setAnalyser(null);
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startLive = (base) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const s = r.current;
    let finals = "";
    const run = () => {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = navigator.language || "en-IN";
      rec.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) finals += `${e.results[i][0].transcript} `;
          else interim += e.results[i][0].transcript;
        }
        s.liveText = `${finals}${interim}`.trim();
        onText(join(base, s.liveText));
      };
      rec.onerror = () => {};
      rec.onend = () => { if (!s.stopLive && r.current === s) setTimeout(run, 150); };
      try { rec.start(); s.rec = rec; } catch { /* live preview optional */ }
    };
    run();
  };

  const start = async () => {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice input needs a secure (https) page in a modern browser.");
      return;
    }
    const mime = pickMime();
    if (mime === null) {
      toast.error("This browser can't record audio. Please update it or type your idea.");
      return;
    }
    setStatus("requesting");
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (err) {
      setStatus("idle");
      toast.error(micErrorMessage(err), { duration: 8000 });
      return;
    }
    const base = getBase();
    const s = { stream, chunks: [], mime, base, liveText: "" };
    r.current = s;

    const Ctx = window.AudioContext || window.webkitAudioContext;
    s.ctx = new Ctx();
    s.ctx.resume?.();
    const an = s.ctx.createAnalyser();
    an.fftSize = 256;
    s.ctx.createMediaStreamSource(stream).connect(an);
    setAnalyser(an);

    const buf = new Uint8Array(an.frequencyBinCount);
    const t0 = Date.now();
    s.levelTimer = setInterval(() => {
      an.getByteFrequencyData(buf);
      s.peak = Math.max(s.peak || 0, ...buf);
      if (Date.now() - t0 > 4000 && s.peak < 25 && !s.warned) {
        s.warned = true;
        toast.warning("We can't hear anything. Check that the right microphone is selected and not muted.", { duration: 7000 });
      }
    }, 250);

    const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    s.recorder = recorder;
    recorder.ondataavailable = (e) => e.data.size && s.chunks.push(e.data);
    recorder.start(1000);
    s.maxTimer = setTimeout(() => stop(), MAX_MS);
    setStatus("recording");
    if (liveCapable()) startLive(base);
  };

  const stop = async () => {
    const s = r.current;
    if (!s.recorder) return;
    s.stopLive = true;
    const done = new Promise((res) => { s.recorder.onstop = res; });
    if (s.recorder.state !== "inactive") s.recorder.stop();
    await done;
    const type = s.recorder.mimeType || s.mime || "audio/webm";
    const blob = new Blob(s.chunks, { type });
    const { base, liveText, peak = 0 } = s;
    cleanup();
    if (blob.size < 1500) {
      setStatus("idle");
      toast.error("No speech was captured. Hold the mic a little longer and speak clearly.");
      return;
    }
    if (peak < 25 && !liveText) {
      setStatus("idle");
      toast.error("No speech detected — your microphone seems silent. Check it's selected and unmuted.");
      return;
    }
    setStatus("transcribing");
    try {
      const { text } = await api.transcribe(blob, extFor(type));
      if (text) onText(join(base, text));
      else if (!liveText) toast.error("We couldn't hear any words. Please speak a little closer to the mic and try again.");
    } catch {
      if (liveText) toast.message("Kept the live transcript — AI clean-up was unavailable.");
      else toast.error("Transcription failed. Please try again or type your idea.");
    }
    setStatus("idle");
  };

  return { status, analyser, start, stop };
}

const join = (base, text) => (base.trim() ? `${base.trim()} ${text}` : text);
